import { FollowUpCategoryTypes } from '@/shared/api/graphql/account';
import type { Consult } from '@/shared/api/graphql/med-consult';
import type { RpmEnrolledAccounts, RpmSetupTypes } from '@/shared/api/graphql/remotePatientMonitoring';
import config from '@/shared/config/config';
import type { RelyingPartyAdminData, SystemOptionType } from '@/shared/lib/types/administration';
import { CcmStatusTypes } from '@/shared/lib/types/chronicCareManagement';
import type { TruentityDateInput } from '@/shared/lib/types/date';
import type { ActivityLogType } from '@/shared/lib/types/graphql';
import type { MuiColor } from '@/shared/lib/types/mui';
import type {
  AccountMedicalServiceEnrollmentSummary,
  AlertLimitsType,
  AlertLimitType,
  CheckboxStateType,
  ClinicalRangesFormValues,
  ContactedType,
  DynamicTemplateType,
  GeneralFollowUpEditModalType,
  MonthYearType,
  ParsedDeviceType,
  ProviderType,
  RefactorActivityLogType,
  RpmAccountSnapshotType,
  RpmFollowupConsultTabType,
  RpmStatusCounts,
  RpmWorkflowTab,
  StatusColors,
  TaskFollowUpEditModalType,
  TemplateInput,
  VitalsDataAvailabilityType
} from '@/shared/lib/types/remotePatientMonitoring';
import {
  AccountRpmMonitoredCondition,
  ActivityMainCategoryType,
  ActivityMainTypeEnum,
  ClaimsCptCodes,
  ConfirmDialogLabelTypes,
  DiagnosisCategories,
  FollowupVisitDueStatusTypes,
  MedConsultStatusType,
  ReviewNoteCategoryType,
  RpmAlertsLabelTypes,
  RpmApprovalStatusTypes,
  RpmDeviceReadingType,
  RpmMonitoringActivityTypes,
  RpmReportReviewTypes,
  RpmSetupStatusTypes,
  RpmSignOffStatus,
  RpmStatusTypes,
  StatusType,
  StatusTypeFilter,
  UnitType,
  VisitTypeEnum,
  VitalsHealthTypes
} from '@/shared/lib/types/remotePatientMonitoring';
import { mandatoryTabsForEnrolment, mandatoryTabsForRpmFollowup, MAX_CARE_ACTIVITY_FUTURE_DAYS, months } from '@/shared/lib/util/constants';
import { calculateTotalSeconds, today } from '@/shared/lib/util/date';
import { FollowupFormModeTypes } from '@/shared/lib/util/followUp';
import ls, { PROVIDER_SESSION_AUTH_KEY } from '@/shared/lib/util/localstorage';
import { pathnameIncludes } from '@/shared/lib/util/location';
import { removeSpaces } from '@/shared/lib/util/string';
import { theme } from '@/shared/styles';
import { color } from '@/shared/styles/assets/colors';
import { FollowUpAddEditDialog, FollowUpTaskAddEditDialog } from '@/shared/ui/components/Dialogs';
import type { JSONData } from '@/shared/ui/JsonKit';
import type { Moment, MomentInput } from 'moment';
import moment from 'moment';
import type React from 'react';
import type { UseFormSetValue } from 'react-hook-form';

export type DateFilterOption = {
  label: string;
  getDateRange: () => { start: Date; end: Date };
};

export const DATE_FILTER_OPTIONS: DateFilterOption[] = [
  {
    label: 'This Month',
    getDateRange: () => {
      const now = new Date();
      return { start: new Date(now.getFullYear(), now.getMonth(), 1), end: now };
    }
  },
  {
    label: 'This Year',
    getDateRange: () => {
      const now = new Date();
      return { start: new Date(now.getFullYear(), 0, 1), end: now };
    }
  }
];

export const LAST_DAYS_OPTIONS: DateFilterOption[] = [
  {
    label: 'Last 30 Days',
    getDateRange: () => {
      const now = new Date();
      const start = new Date();
      start.setDate(now.getDate() - 30);
      return { start, end: now };
    }
  },
  {
    label: 'Last 60 Days',
    getDateRange: () => {
      const now = new Date();
      const start = new Date();
      start.setDate(now.getDate() - 60);
      return { start, end: now };
    }
  },
  {
    label: 'Last 90 Days',
    getDateRange: () => {
      const now = new Date();
      const start = new Date();
      start.setDate(now.getDate() - 90);
      return { start, end: now };
    }
  }
];

export const SELECT_DEFAULT_OPTION: { value: string; label: string } = { value: 'all', label: 'All' };
export const currentYear = today().year();
export const currentMonth = today().month();

export const DEFAULT_DEVICE_LENGTH = 2;

// Devices are all Tenovi, so the brand adds nothing in a patient list: "tenovi glucometer" → "glucometer".
// Names are stored lowercase, hence the case-insensitive match.
export const getDeviceDisplayName = (name?: string | null) => (name ?? '').replace(/^tenovi\s+/i, '');

export const PROVIDER_APPROVAL_COMMENT_END_TEXT = 'control and improve their long-term health outcomes. \n';

export const DEFAULT_VITALS_SUMMARY_RANGE_MONTHS = 6;

/**
 * `labelColor` is for the words and `iconColor` for the triangle. They differ only where the
 * severity colour is too light to read as text: the yellows and ambers stay bright on the icon
 * (AlertTypeIcon puts them on a dark tile) while the label takes a darker tone that clears 4.5:1.
 */
export const getAlertTypeStyles = (value: string): { labelText: string; labelColor: string; iconColor: string } => {
  let labelText = '';
  let labelColor = '';
  let iconColor = '';
  if (value === RpmAlertsLabelTypes.CRITICALLY_HIGH) {
    labelColor = iconColor = color.red900;
    labelText = 'Critically High';
  } else if (value === RpmAlertsLabelTypes.CRITICALLY_LOW) {
    labelColor = color.alertTextOrange;
    iconColor = color.amber800;
    labelText = 'Critically Low';
  } else if (value === RpmAlertsLabelTypes.HIGH) {
    labelColor = color.red800;
    iconColor = color.errorMain;
    labelText = 'Abnormal High';
  } else if (value === RpmAlertsLabelTypes.LOW) {
    labelColor = color.alertTextAmber;
    iconColor = color.warningDark;
    labelText = 'Abnormal Low';
  } else if (value === RpmAlertsLabelTypes.RPM_SETUP) {
    labelColor = iconColor = color.truentityBlue[800];
    // "Patient Setup" rather than "RPM Setup": the alert is raised on an incomplete onboarding,
    // which is not a thing the reviewer thinks of as belonging to RPM specifically.
    labelText = 'Patient Setup';
  } else if (value === RpmAlertsLabelTypes.NO_READINGS) {
    labelColor = iconColor = color.grey700;
    labelText = 'No Readings';
  } else if (value === RpmAlertsLabelTypes.MED_REGIMEN) {
    labelColor = color.truentityCyan[700];
    iconColor = color.truentityCyan[500];
    labelText = 'Med Regimen';
  } else if (value === RpmAlertsLabelTypes.REPORT_FAX_FAILED) {
    labelColor = color.alertTextAmber;
    iconColor = color.warningDark;
    labelText = 'Report Fax Failed';
  }
  return { labelText, labelColor, iconColor };
};

/*
 * Whether a reading alert is about a value running high or low, so its label can step the last
 * word up or down. Null for alerts that are not about a reading.
 */
export const getAlertDirection = (value: string): 'high' | 'low' | null => {
  switch (value) {
    case RpmAlertsLabelTypes.CRITICALLY_HIGH:
    case RpmAlertsLabelTypes.HIGH:
      return 'high';
    case RpmAlertsLabelTypes.CRITICALLY_LOW:
    case RpmAlertsLabelTypes.LOW:
      return 'low';
    default:
      return null;
  }
};

/**
 * Which readings tab an alert belongs to, from its `alertType`.
 *
 * The reason strings name their vital -- CRITICALLY_HIGH_BLOOD_GLUCOSE, LOW_HEART_RATE,
 * HIGH_SYSTOLIC_BLOOD_PRESSURE -- so a substring is enough and stays correct as new reasons are
 * added for a vital that already exists. (One of them is spelled DIATOLIC; it still carries
 * BLOOD_PRESSURE, so it lands in the right place regardless.)
 *
 * Null for the alerts that are not about a reading at all: no readings, RPM setup, med regimen.
 * Those have no tab to switch to.
 */
export const getVitalsHealthTypeForAlertType = (alertType?: string | null): VitalsHealthTypes | null => {
  if (!alertType) return null;

  if (alertType.includes('BLOOD_GLUCOSE')) return VitalsHealthTypes.BloodGlucose;
  if (alertType.includes('HEART_RATE')) return VitalsHealthTypes.HeartRate;
  if (alertType.includes('BLOOD_PRESSURE')) return VitalsHealthTypes.BloodPressure;

  return null;
};

export const getReadingStatusStyles = (label: StatusTypeFilter): StatusColors => {
  const colors: Record<StatusTypeFilter, StatusColors> = {
    [StatusTypeFilter.CRITICALLY_LOW]: {
      textColor: color.white,
      bgColor: color.amber800
    },
    [StatusTypeFilter.LOW]: {
      textColor: color.white,
      bgColor: color.warningDark
    },
    [StatusTypeFilter.NORMAL]: {
      textColor: color.white,
      bgColor: color.green800
    },
    [StatusTypeFilter.HIGH]: {
      textColor: color.white,
      bgColor: color.errorMain
    },
    [StatusTypeFilter.CRITICALLY_HIGH]: {
      textColor: color.white,
      bgColor: color.red900
    }
  };

  return colors[label] || { textColor: 'inherit', bgColor: 'inherit' };
};

export const vitalsTableName = (unitType: UnitType): VitalsHealthTypes => {
  switch (unitType) {
    case UnitType.HeartRate:
      return VitalsHealthTypes.HeartRate;
    case UnitType.BloodPressure:
      return VitalsHealthTypes.BloodPressure;
    case UnitType.BloodGlucose:
      return VitalsHealthTypes.BloodGlucose;
  }
};

export const rpmStatusMapping = {
  0: RpmStatusTypes.SCHEDULE_FOR_REVIEW,
  1: RpmStatusTypes.IN_REVIEW,
  2: RpmStatusTypes.APPROVED,
  3: RpmStatusTypes.REJECTED,
  4: RpmStatusTypes.SKIPPED
};

export const getRpmWorkflowSessionType = () => {
  if (pathnameIncludes('patient-setup/care-activity')) {
    return RpmMonitoringActivityTypes.CareActivity;
  } else if (pathnameIncludes('patient-setup/encounter')) {
    return RpmMonitoringActivityTypes.Encounter;
  }
  return undefined;
};

export const getRpmStatusCount = (tabLabel: string, statusCounts: RpmStatusCounts): number => {
  return statusCounts[tabLabel as RpmStatusTypes] || 0;
};

export const getProviderSessionUrl = (sessionId: string) => {
  return `${window.location.origin.toString()}/rpm/${sessionId}`;
};

export const mapRpmStatusToColor = (status: string | undefined): string => {
  switch (status) {
    case RpmStatusTypes.REJECTED:
      return theme.palette.error.main;
    case RpmStatusTypes.IN_REVIEW:
      return theme.palette.grey[400];
    case RpmStatusTypes.SKIPPED:
      return theme.palette.warning.main;
    case RpmStatusTypes.APPROVED:
      return theme.palette.success.main;
    default:
      return theme.palette.grey[300];
  }
};

export const setRpmProviderAuthCode = (authCode: string) => {
  ls.set(PROVIDER_SESSION_AUTH_KEY, authCode);
};

export const getRpmProviderAuthCode = () => {
  return ls.get(PROVIDER_SESSION_AUTH_KEY);
};

export const removeRpmProviderAuthCode = () => {
  ls.remove(PROVIDER_SESSION_AUTH_KEY);
};

export const setRpmProviderReportsIds = (reportIds: string[]) => {
  sessionStorage.setItem('reportsIds', JSON.stringify(reportIds));
};

export const getRpmProviderReportsIds = () => {
  return sessionStorage.getItem('reportsIds') ?? '';
};

export const resetProviderSession = () => {
  removeRpmProviderAuthCode();
  sessionStorage.removeItem('reportsIds');
};

export const mapRpmDialogLabelToStatus = (status: ConfirmDialogLabelTypes): string | undefined => {
  switch (status) {
    case ConfirmDialogLabelTypes.REJECT:
      return RpmStatusTypes.REJECTED;
    case ConfirmDialogLabelTypes.APPROVE:
      return RpmStatusTypes.APPROVED;
    case ConfirmDialogLabelTypes.SKIP:
      return RpmStatusTypes.SKIPPED;
  }
};

export const getApprovalStatusChipStyles = (
  approvalStatus: string,
  rpmSignOffStatus?: string
): {
  color: string;
  text: string;
} => {
  if (approvalStatus === RpmApprovalStatusTypes.APPROVED) {
    const signOffStatusText = mapRpmSignOffStatus({
      rpmSignOffStatus
    });

    return {
      color: manRpmSignOffStatusColor({ rpmSignOffStatus }),
      text: `${signOffStatusText ? `Approved ${signOffStatusText}` : 'Approved'}`
    };
  } else if (approvalStatus === RpmApprovalStatusTypes.REJECTED) {
    return {
      color: color.errorMain,
      text: 'Rejected'
    };
  } else if (approvalStatus === RpmApprovalStatusTypes.IN_REVIEW) {
    return {
      color: color.orangeDarker,
      text: 'In Review'
    };
  } else if (approvalStatus === RpmApprovalStatusTypes.PENDING) {
    return {
      color: color.grey400,
      text: 'Pending'
    };
  } else if (approvalStatus === RpmApprovalStatusTypes.SCHEDULE_FOR_REVIEW) {
    return {
      color: color.warningDark,
      text: 'Scheduled'
    };
  } else if (approvalStatus === RpmApprovalStatusTypes.SKIPPED) {
    return {
      color: color.secondaryDark,
      text: 'Incomplete'
    };
  } else {
    return {
      color: color.primaryDark,
      text: 'Other'
    };
  }
};

export const getColorForTotalReadings = (totalReadings: number): 'success' | 'error' | 'info' => {
  if (totalReadings >= 16) {
    return 'success';
  } else if (totalReadings >= 8) {
    return 'info';
  } else {
    return 'error';
  }
};

export const getDeviceReadingsColors = new Map<string, { backgroundColor: string; color: string }>([
  ['TARGET_MET', { backgroundColor: '#1E90FF', color: '#fff' }],
  ['TARGET_MISSED', { backgroundColor: '#AFAFB5', color: '#fff' }],
  ['HIGH_RISK_OF_MISSING_TARGET', { backgroundColor: '#EB5353', color: '#fff' }],
  ['BEHIND_TARGET', { backgroundColor: '#F1C40F', color: '#fff' }],
  ['ON_TARGET', { backgroundColor: '#387F39', color: '#fff' }]
]);

export const getActivityBasedColors = new Map<string, { backgroundColor: string; color: string }>([
  ['MORE_THAN_OR_EQUAL_80', { backgroundColor: '#387F39', color: '#fff' }],
  ['BETWEEN_60_TO_79', { backgroundColor: '#97BE67', color: '#fff' }],
  ['BETWEEN_40_TO_59', { backgroundColor: '#BEDC74', color: '#000' }],
  ['BETWEEN_20_TO_39', { backgroundColor: '#F1C40F', color: '#fff' }],
  ['LESS_THAN_20', { backgroundColor: '#EB5353', color: '#fff' }]
]);

export const getColorForMonthlyTimeSpent = (time: number): 'success' | 'error' | 'info' => {
  if (typeof time !== 'number') return 'error';
  if (time < 1200) return 'info';
  return 'success';
};

export const getContentAndTargetTab = (statusType: RpmStatusTypes, confirmButtonText: string) => {
  let content = '';
  let targetTab = '';

  if (statusType === RpmStatusTypes.READY_FOR_REVIEW) {
    content = ConfirmDialogLabelTypes.SEND_SCHEDULE_FOR_REVIEW;
    targetTab = RpmStatusTypes.SCHEDULE_FOR_REVIEW;
  } else if (statusType === RpmStatusTypes.IN_REVIEW) {
    if (confirmButtonText === ConfirmDialogLabelTypes.RESEND_FOR_REVIEW) {
      content = ConfirmDialogLabelTypes.RESEND_FOR_REVIEW;
      targetTab = RpmStatusTypes.IN_REVIEW;
    }
  }

  return { content, targetTab };
};
export const statusColorMapping = new Map<string, MuiColor>([
  [StatusType.Initial, 'info'],
  [StatusType.Connecting, 'warning'],
  [StatusType.Connected, 'success']
]);

export const vendorStatusColorMapping = new Map<string, MuiColor>([
  ['Connected', 'success'],
  ['Replaced', 'success'],
  ['Delivered', 'info'],
  ['Shipped', 'primary'],
  ['Pending Shipment', 'warning'],
  ['Available for Pickup', 'secondary'],
  ['Unlinked', 'error']
]);

export const setCheckboxState = (
  stateSetter: React.Dispatch<React.SetStateAction<CheckboxStateType>>,
  checked: boolean,
  disabled: boolean
) => {
  stateSetter({ checked, disabled });
};

export const refactorActivityLog = (activityLogs: ActivityLogType[]) => {
  const temporarySteps: RefactorActivityLogType[] = [];
  const dateToStepMap: Record<string, RefactorActivityLogType> = {};
  if (activityLogs) {
    activityLogs.forEach(item => {
      const date = new Date(item.activityTime);
      const dateString = date.toDateString();

      if (!dateToStepMap[dateString]) {
        const step: RefactorActivityLogType = {
          id: temporarySteps.length + 1 + '',
          logText: date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
          children: []
        };
        dateToStepMap[dateString] = step;

        temporarySteps.push(step);
      }
      const step = dateToStepMap[dateString];
      if (item.timeTakenSecs) {
        // Pre-existing quirk, deliberately preserved: children use `logText`/string labelDesc
        // (not RefactorActivityLogType's `label`/number shape), and step.labelDesc is never
        // initialized so `undefined += <number>` coerces to NaN. See rpm.test.ts's
        // characterization test for `refactorActivityLog`.
        (step.children as unknown[]).push({
          id: item.id,
          logText: item.logText,
          labelDesc: `${item.timeTakenSecs}`
        });
        (step as unknown as { labelDesc: number }).labelDesc += item.timeTakenSecs;
      }
    });
  }

  return temporarySteps;
};

export const generateMonthList = (startYear: number, startMonth: string): MonthYearType[] => {
  const result: MonthYearType[] = [];

  for (let year = currentYear; year >= startYear; year--) {
    const monthYear: MonthYearType = {
      year: year.toString(),
      months: []
    };

    const startMonthIndex = year === currentYear ? currentMonth : 11;

    for (let monthIndex = startMonthIndex; monthIndex >= 0; monthIndex--) {
      if (year === startYear && monthIndex < months.indexOf(startMonth)) {
        break;
      }

      monthYear.months.push(months[monthIndex]);
    }

    result.push(monthYear);
  }

  return result;
};

export const generateMonthListFromDate = (startDate: Date) => {
  const startYear = startDate.getFullYear();
  const startMonth = startDate.toLocaleString('en-US', { month: 'long' });

  return generateMonthList(startYear, startMonth);
};

/**
 * "Noted During" (account_notes.mode_of_capture). ClickUp 86eydgy6u.
 *
 * `id` is the value stored in the database and must stay byte-identical to
 * NoteModeOfCaptureType on the backend, which is the source of truth. It used to be an
 * uppercase pseudo-id, but SelectList lowercases option values unless the caller passes
 * `keepOriginalValue`, so picks were stored as 'chart review' while other flows wrote
 * Title case. Both note dialogs now pass that flag and send the canonical string.
 *
 * These are only the staff-selectable values. The seven remaining canonical values
 * (Activity: Device & Readings, Activity: Patient Education, Activity: Medication
 * Management Support, Activity: Care Coordination, Activity: Care Plan Support,
 * Activity: Imported, Activity: Unenroll Patient) are written by the system and are
 * deliberately absent here: offering them would let staff label a manual note as coming
 * from a flow it never touched.
 */
export const modeOfCaptureNoteList = [
  {
    id: 'Talk with Patient',
    name: 'Talk with Patient'
  },
  {
    id: 'Talk with Patient/Caregiver',
    name: 'Talk with Patient/Caregiver'
  },
  {
    id: 'Talk with Patient/Caregiver: In-Person',
    name: 'Talk with Patient/Caregiver: In-Person'
  },
  {
    id: 'Text with Patient',
    name: 'Text with Patient'
  },
  {
    id: 'Talk with Provider',
    name: 'Talk with Provider'
  },
  {
    id: 'Activity: Chart Review',
    name: 'Activity: Chart Review'
  },
  {
    id: 'Activity: Left Voicemail',
    name: 'Activity: Left Voicemail'
  },
  {
    id: 'Other',
    name: 'Other'
  }
];

export const filterByNoteList = [
  {
    id: 'ALWAYS SHARE',
    name: 'Always Share'
  }
];

/**
 * The alerts page's date window. "All time" is the default, because the list is a backlog to work
 * through rather than a monthly report -- an alert raised in August is still open in September.
 */
export enum RpmAlertsDateRange {
  ALL_TIME = 'allTime',
  THIS_MONTH = 'thisMonth'
}

/**
 * The `start_date` / `end_date` the alerts queries take, or nothing at all for "all time".
 *
 * Full datetimes rather than dates: both queries compare against `created_at`, so an end of
 * "2026-09-30" would drop every alert raised after midnight on the last day of the month.
 */
export const getAlertsDateRangeFilter = (range: RpmAlertsDateRange): { startDate?: string; endDate?: string } => {
  if (range !== RpmAlertsDateRange.THIS_MONTH) return {};

  return {
    startDate: moment().startOf('month').format('YYYY-MM-DD HH:mm:ss'),
    endDate: moment().endOf('month').format('YYYY-MM-DD HH:mm:ss')
  };
};

export enum RpmAlertsReadStatus {
  ALL = 'all',
  READ = 'read',
  UNREAD = 'unread'
}

export const rpmAlertsReadOptions: Array<{ label: string; value: string }> = [
  {
    label: 'All',
    value: RpmAlertsReadStatus.ALL
  },
  {
    label: 'Read',
    value: RpmAlertsReadStatus.READ
  },
  {
    label: 'Unread',
    value: RpmAlertsReadStatus.UNREAD
  }
];

export const rpmStatusOptions: Array<{ label: string; value: string }> = [
  {
    label: 'Enrolled',
    value: RpmStatusTypes.ENROLLED
  },
  {
    label: 'Unenrolled',
    value: RpmStatusTypes.UNENROLLED
  },
  {
    label: 'Candidate',
    value: RpmStatusTypes.IS_CANDIDATE
  },
  {
    label: 'Schedule for enrollment',
    value: RpmStatusTypes.SCHEDULED_FOR_ENROLLMENT
  }
];

export const activityCategoryList = [
  {
    id: ActivityMainCategoryType.SETUP,
    name: ActivityMainCategoryType.SETUP
  },
  {
    id: ActivityMainCategoryType.MONTHLY_CARE_MANAGEMENT,
    name: ActivityMainCategoryType.MONTHLY_CARE_MANAGEMENT
  }
];

export const activityTypeList = [
  {
    id: 'RPM',
    name: 'RPM'
  }
];

export const mapRpmReportReviewStatusToColor = (status: string | undefined): string => {
  switch (status) {
    case RpmReportReviewTypes.PENDING:
    case RpmReportReviewTypes.IN_PROGRESS:
      return theme.palette.grey[400];
    case RpmReportReviewTypes.REJECTED:
      return theme.palette.warning.main;
    case RpmReportReviewTypes.COMPLETED:
      return theme.palette.success.main;
    default:
      return '';
  }
};

export const fetchDocumentUrl = async (reportDownloadUrl: string): Promise<string> => {
  try {
    const response = await fetch(`${reportDownloadUrl}`);
    const blob = await response.blob();
    return URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
  } catch (err) {
    console.error(err);
    return '';
  }
};

export const MedConsultStatusColorMapping = new Map<string, MuiColor>([
  [MedConsultStatusType.IN_PROGRESS, 'warning'],
  [MedConsultStatusType.ARCHIVED, 'info'],
  [MedConsultStatusType.ENDED, 'success']
]);

export function replaceUnderscoresWithSpaces(inputString: string): string {
  return inputString.toLowerCase().replace(/_/g, ' ');
}

export const getAlertLimitsArray = (alertLimits: AlertLimitType): [number, number, number, number] => {
  return [Number(alertLimits.cLowValue), Number(alertLimits.lowValue), Number(alertLimits.highValue), Number(alertLimits.cHighValue)];
};

export const getAlertLimitsObject = (alertLimits: [number, number, number, number]): AlertLimitType => {
  return {
    cLowValue: alertLimits[0]?.toString(),
    lowValue: alertLimits[1]?.toString(),
    highValue: alertLimits[2]?.toString(),
    cHighValue: alertLimits[3]?.toString()
  } as AlertLimitType;
};

export const refactorNotAvailableRpmSetups = (notAvailableRpmSetups: string[]) => {
  return notAvailableRpmSetups.map(notAvailableTab => ({
    key: notAvailableTab,
    isRequiredForEnrollment: mandatoryTabsForEnrolment.includes(notAvailableTab)
  }));
};

export const enrollmentEligibility = (rpmSetupTabs: RpmSetupTypes[], notConsiderForRpmEnrollments?: RpmWorkflowTab[]) => {
  const filteredTabs = rpmSetupTabs.filter(
    setup => !notConsiderForRpmEnrollments || !notConsiderForRpmEnrollments.some(notConsiderTab => notConsiderTab === setup.type)
  );

  const mandatoryTypeArray = filteredTabs.filter(setup => setup.isRequiredForEnrollment);
  return mandatoryTypeArray.every(mandatoryType => mandatoryType.status === RpmSetupStatusTypes.COMPLETED);
};

/** The setup tabs a patient must complete to enroll, without the tabs hidden on their path. */
export const enrollmentMandatoryTabs = (hiddenTabs?: RpmWorkflowTab[]) =>
  mandatoryTabsForEnrolment.filter(tabType => !hiddenTabs?.includes(tabType as RpmWorkflowTab));

export const getSelectedTabData = (rpmSetupTabs: RpmSetupTypes[], selectedTabType: RpmWorkflowTab) => {
  return rpmSetupTabs.find(setupTab => setupTab.type === selectedTabType);
};

export const updateRpmTabStatus = async (
  rpmSetupTabs: RpmSetupTypes[],
  tabType: RpmWorkflowTab,
  editRpmStatus: (value: RpmSetupTypes) => void,
  updateRpmSetup: any,
  isOverrideAvailable = true,
  status: RpmSetupStatusTypes = RpmSetupStatusTypes.COMPLETED
) => {
  const selectedTabData = getSelectedTabData(rpmSetupTabs, tabType);
  let isUpdatable = isOverrideAvailable;

  if (!isOverrideAvailable) {
    isUpdatable = !selectedTabData?.isManualStatusUpdate;
  }

  if (selectedTabData?.id && selectedTabData.status !== status && isUpdatable) {
    try {
      const res = await updateRpmSetup({
        variables: {
          rpmSetupId: selectedTabData.id,
          status
        }
      });

      const { rpmSetup, status: responseStatus } = res.data!.updateRpmSetup;
      const variant = responseStatus === 'Success' ? 'success' : 'error';

      if (variant === 'success') {
        editRpmStatus({ ...rpmSetup });
        return true;
      }
    } catch (error) {
      console.error('Failed to update RPM setup:', error);
      return false;
    }
  }
  return false;
};

export const areAllFieldsFilled = (values: Record<string, string | boolean | Moment | object>): boolean => {
  return Object.values(values).every(value => {
    if (typeof value === 'boolean') {
      return value;
    }

    if (typeof value === 'string') {
      return value.trim() !== '';
    }

    if (moment.isMoment(value)) {
      return value.isValid();
    }

    if (typeof value === 'object' && value !== null) {
      const entries = Object.entries(value).filter(([key]) => key !== '__typename');

      if (entries.length === 0) {
        return true;
      }
      return areAllFieldsFilled(Object.fromEntries(entries) as Record<string, string | boolean | Moment | object>);
    }

    return false;
  });
};

export const getClaimsChipColor = (cptCode: string): { backgroundColor: string; textColor: string } => {
  let backgroundColor = '';
  let textColor = '';

  switch (cptCode) {
    case ClaimsCptCodes.RPM_MONTHLY_MONITORING_CODE:
      backgroundColor = color.primaryLight;
      textColor = color.primaryMain;
      break;
    case ClaimsCptCodes.RPM_SETUP_CODE:
      backgroundColor = color.secondary100;
      textColor = color.secondaryDark;
      break;
    case ClaimsCptCodes.RPM_MONTHLY_MONITORING_ADDITIONAL_CODE:
      backgroundColor = color.secondaryLight;
      textColor = color.secondaryDark;
      break;
    case ClaimsCptCodes.RPM_DAILY_MONITORING_CODE:
      backgroundColor = color.primary100;
      textColor = color.primaryMain;
      break;
  }

  return { backgroundColor, textColor };
};

export const setAlertLimits = (alertLimits: AlertLimitsType, setValue: UseFormSetValue<ClinicalRangesFormValues>) => {
  if (alertLimits) {
    if (alertLimits.heartRate) {
      setValue('heartRateLimits', getAlertLimitsArray(alertLimits.heartRate));
    }
    if (alertLimits.bloodGlucose) {
      setValue('bloodGlucoseLimits', getAlertLimitsArray(alertLimits.bloodGlucose));
    }
    if (alertLimits.sysBloodPressure) {
      setValue('sysBloodPressureLimits', getAlertLimitsArray(alertLimits.sysBloodPressure));
    }
    if (alertLimits.diaBloodPressure) {
      setValue('diaBloodPressureLimits', getAlertLimitsArray(alertLimits.diaBloodPressure));
    }
  }
};

export const formatErrorMessage = (error: unknown) => {
  if (error instanceof Error) {
    return error.message;
  } else if (typeof error === 'string') {
    return error;
  } else if (error && typeof error === 'object' && 'message' in error) {
    return (error as { message: string }).message;
  }
  return 'An error occurred';
};

export const getProviderFullName = (provider: Partial<ProviderType>) => {
  return `${provider.individualFirstName ?? ''} ${provider.individualLastName ?? ''}`;
};

// Responses are stored as { value, note } JSON (survey + popover). Render them
// readably and tolerate legacy plain-text responses.
const formatConsultResponse = (responseText?: string | null) => {
  if (!responseText) return '---';

  try {
    const parsed = JSON.parse(responseText);
    if (parsed && typeof parsed === 'object') {
      // A conditional question skipped because of an earlier answer (requiredWhen).
      if (parsed.skipped === true) return 'Not applicable';
      const value = typeof parsed.value === 'string' ? parsed.value : '';
      const note = typeof parsed.note === 'string' ? parsed.note : '';
      const text = [value, note ? `(${note})` : ''].filter(Boolean).join(' ');
      return text || '---';
    }
  } catch {
    // Legacy plain-text response — fall through.
  }

  return responseText;
};

export const medConsultToString = (consult: Consult) => {
  let result = '';

  consult?.items.forEach(item => {
    result += `Q: ${item.primaryText}\n`;
    result += `A: ${formatConsultResponse(item.responseText)}\n\n`;
  });

  return result;
};

const parseDevice = (device: any): ParsedDeviceType => ({
  id: device['id'],
  latestReadings:
    device['latest_readings']?.map((reading: any) => ({
      value: reading['value'],
      unit: reading['unit'],
      key: reading['key'],
      recordedAt: reading['recorded_at'],
      createdAt: reading['created_at']
    })) || [],
  monitoringDevice: device['monitoring_device']
    ? {
        id: device['monitoring_device']['id'],
        brandName: device['monitoring_device']['brand_name'],
        name: device['monitoring_device']['name'],
        typeName: device['monitoring_device']['type_name']
      }
    : { id: '', brandName: '', name: '', typeName: '' }
});

const getRpmAccountSnapshot = ({
  devices: deviceJsonData,
  account,
  ...otherData
}: RpmEnrolledAccounts): RpmAccountSnapshotType | undefined => {
  try {
    const deviceJson: JSONData = deviceJsonData ? deviceJsonData : {};
    return {
      id: account?.truentityId,
      truentityId: account?.truentityId,
      user: account?.user,
      birthDate: account?.birthDate,
      zipcode: account?.zipcode,
      rpmEnrolledAt: account?.rpmEnrolledAt,
      rpmUnenrolledAt: account?.rpmUnenrolledAt,
      lastMedrecDate: account?.lastMedrecDate,
      rpmApprovalStatus: account?.rpmApprovalStatus,
      rpmSignOffStatus: account?.rpmSignOffStatus,
      setting: account?.setting,
      healthPlan: account.healthPlan,
      tags: account?.tags, // Add the missing tags mapping
      billingHoldActive: account?.billingHoldActive,
      activeBillingHoldReasons: account?.activeBillingHoldReasons,
      visibleBillingHolds: account?.visibleBillingHolds,
      eligibilityReview: account?.eligibilityReview ?? null,
      smsOptOut: account?.smsOptOut,
      lastContacted: {
        type: (otherData.lastContactedType || '') as ContactedType,
        date: otherData.lastContactedDate || ''
      },
      phone: account?.phone || '',
      rpmStatus: account?.rpmStatus,
      ccmStatus: account?.ccmStatus ?? null,
      rpmTime: otherData.rpmMonthlyTimeSpent ?? null,
      ccmTime: otherData.ccmMonthlyTimeSpent ?? null,
      isRpmReportAvailable: otherData?.isRpmReportAvailable,
      monthlyTimeSpent: otherData.monthlyTimeSpent ?? 0,
      monthlyTotalReadings: otherData.monthlyTotalReadings ?? 0,
      lastLogBpAt: otherData?.lastLogBpAt ?? null,
      lastLogBgAt: otherData?.lastLogBgAt ?? null,
      lastLogHrAt: otherData?.lastLogHrAt ?? null,
      followupVisitDueStatus: otherData?.followupVisitDueStatus,
      isLastVisitAsync: otherData?.isLastVisitAsync,
      lastCompletedFollowupVisitOn: otherData?.lastCompletedFollowupVisitOn,
      lastActivityAt: otherData?.lastActivityAt,
      lastActivityDoneBy: otherData?.lastActivityDoneBy,
      devices: Array.isArray(deviceJson) ? deviceJson.map(parseDevice) : [],
      readingBasedCategoryName: otherData?.readingBasedCategoryName ?? '',
      activityBasedCategoryName: otherData?.activityBasedCategoryName ?? ''
    };
  } catch (error) {
    console.error(error);
    return undefined;
  }
};

export const mapEnrolledAccounts = (rpmEnrolledAccounts: RpmEnrolledAccounts[]): RpmAccountSnapshotType[] => {
  return rpmEnrolledAccounts?.map(getRpmAccountSnapshot).filter((snapshot): snapshot is RpmAccountSnapshotType => snapshot !== undefined);
};

export const extractValueFromJson = (json: string, key: string) => {
  try {
    const jsonSetting = JSON.parse(json);
    const extractedValue = jsonSetting[key] || undefined;
    return extractedValue;
  } catch (error) {
    console.error('Encountered an error while extracting data from JSON:', error);
    return undefined;
  }
};

type MapRpmSignOffStatusProps = {
  rpmSignOffStatus?: string;
};

export const mapRpmSignOffStatus = ({ rpmSignOffStatus }: MapRpmSignOffStatusProps): string | null => {
  if ((rpmSignOffStatus?.length ?? 0) > 0) {
    switch (rpmSignOffStatus as RpmSignOffStatus) {
      case RpmSignOffStatus.ASYNCHRONOUSLY_APPROVED:
        return 'Asynchronous';
      case RpmSignOffStatus.TELEHEALTH_FOLLOWUP_VISIT_APPROVED:
        return 'Telehealth Follow-up Visit';
      case RpmSignOffStatus.TELEHEALTH_INITIAL_VISIT_APPROVED:
        return 'Telehealth New Patient Visit';
      default:
        return null;
    }
  } else {
    return null;
  }
};

export const manRpmSignOffStatusColor = ({ rpmSignOffStatus }: MapRpmSignOffStatusProps): string => {
  if ((rpmSignOffStatus?.length ?? 0) > 0) {
    switch (rpmSignOffStatus as RpmSignOffStatus) {
      case RpmSignOffStatus.ASYNCHRONOUSLY_APPROVED:
        return color.successDark;
      case RpmSignOffStatus.TELEHEALTH_FOLLOWUP_VISIT_APPROVED:
        return color.success500;
      case RpmSignOffStatus.TELEHEALTH_INITIAL_VISIT_APPROVED:
        return color.success700;
      default:
        return color.grey500;
    }
  } else {
    return color.grey500;
  }
};

export const getConditionStrings = (conditions: string[] | undefined) => {
  if (!conditions || conditions.length === 0) return AccountRpmMonitoredCondition.UNASSIGNED;

  const hasBloodGlucose = conditions.includes('Blood Glucose');
  const hasBloodPressure = conditions.includes('Blood Pressure');

  if (hasBloodGlucose && hasBloodPressure) {
    return AccountRpmMonitoredCondition.DIABETES_AND_HYPERTENSION;
  } else if (hasBloodGlucose) {
    return AccountRpmMonitoredCondition.DIABETES;
  } else if (hasBloodPressure) {
    return AccountRpmMonitoredCondition.HYPERTENSION;
  }

  return AccountRpmMonitoredCondition.UNASSIGNED;
};

export const getDiagnosisUsingConditions = (condition: AccountRpmMonitoredCondition) => {
  let conditions: DiagnosisCategories[] = [];

  switch (condition) {
    case AccountRpmMonitoredCondition.DIABETES_AND_HYPERTENSION:
      conditions = [DiagnosisCategories.DIABETES, DiagnosisCategories.HYPERTENSION];
      break;
    case AccountRpmMonitoredCondition.DIABETES:
      conditions = [DiagnosisCategories.DIABETES];
      break;
    case AccountRpmMonitoredCondition.HYPERTENSION:
      conditions = [DiagnosisCategories.HYPERTENSION];
      break;
    default:
      conditions = [];
      break;
  }

  return conditions;
};

export function formatRpmStatusString(text: string) {
  if (text === RpmStatusTypes.SCHEDULED_FOR_ENROLLMENT) return 'Scheduled';

  const words = text.split('_');
  const formattedWords = words.map(word => {
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  });
  const formattedString = formattedWords.join(' ');

  return formattedString;
}

type GetMeetUrlUsernameProps = {
  firstName?: string;
  lastName?: string;
  relyingPartyName?: string;
};

type RedirectToDoxyUrlProps = GetMeetUrlUsernameProps & {
  visitType: VisitTypeEnum;
};

type GetMeetUrlWithParamsProps = GetMeetUrlUsernameProps & {
  meetUrl: string;
};

export const getMeetUrlUsername = ({ firstName, lastName, relyingPartyName }: GetMeetUrlUsernameProps) => {
  const enFirstName = encodeURIComponent(removeSpaces(firstName || ''));
  const enLastName = encodeURIComponent(removeSpaces(lastName || ''));
  const enRelyingPartyName = encodeURIComponent(removeSpaces(relyingPartyName || ''));

  return `${enFirstName}-${enLastName}-(${enRelyingPartyName})`;
};

export const getMeetUrlWithParams = ({ firstName, lastName, relyingPartyName, meetUrl }: GetMeetUrlWithParamsProps) => {
  const username = getMeetUrlUsername({ firstName, lastName, relyingPartyName });
  return `${meetUrl}?username=${username}&autocheckin=true`;
};

export const redirectToDoxyUrl = ({ firstName, lastName, relyingPartyName, visitType }: RedirectToDoxyUrlProps) => {
  const meetUrl = visitType === VisitTypeEnum.INITIAL_VISIT ? config.TRUENTITY_INITIAL_VISIT_URL : config.TRUENTITY_FOLLOWUP_VISIT_URL;

  if (meetUrl) {
    const meetUrlWithParams = getMeetUrlWithParams({ firstName, lastName, relyingPartyName, meetUrl });

    window.open(meetUrlWithParams, '_blank');
  } else {
    console.error('Doxy URL is not set');
  }
};

export const getFollowupConsultEligibility = (
  rpmFollowupConsultTabs: Pick<
    Record<RpmWorkflowTab, RpmFollowupConsultTabType>,
    RpmWorkflowTab.CLINICAL_SUMMARY | RpmWorkflowTab.MEDICAL_CONSULTS | RpmWorkflowTab.CARE_PLAN | RpmWorkflowTab.MEDICAL_RECONCILIATION
  >
): boolean => {
  return mandatoryTabsForRpmFollowup.every(
    tab => rpmFollowupConsultTabs[tab as keyof typeof rpmFollowupConsultTabs]?.status === RpmSetupStatusTypes.COMPLETED
  );
};

const isStartTimeGreaterThanEnrolledDate = (startTime: MomentInput, enrolledAtDate: MomentInput): boolean => {
  if (startTime) {
    const startTimeDate = startTime as Moment;
    return startTimeDate.isAfter(enrolledAtDate);
  } else {
    return false;
  }
};

export const FUTURE_START_TIME_ERROR_MESSAGE = `Reviewed Datetime can't be more than ${MAX_CARE_ACTIVITY_FUTURE_DAYS} days in the future.`;

export const getMaxCareActivityStartTime = (): Moment => moment().add(MAX_CARE_ACTIVITY_FUTURE_DAYS, 'days');

export const isStartTimeWithinFutureLimit = (startTime: MomentInput): boolean =>
  !!startTime && moment(startTime).isSameOrBefore(getMaxCareActivityStartTime());

type ValidateCareActivityTimeInputsProps = {
  startTime: MomentInput;
  timeMinutes: number | string | null;
  enrolledAtDate: MomentInput;
};

export const validateCareActivityTimeInputs = ({
  startTime,
  timeMinutes,
  enrolledAtDate
}: ValidateCareActivityTimeInputsProps): boolean => {
  if (!startTime) {
    return false;
  }

  const minutes = typeof timeMinutes === 'string' ? parseFloat(timeMinutes) : timeMinutes;
  if (calculateTotalSeconds(minutes) < 1) {
    return false;
  }

  return isStartTimeGreaterThanEnrolledDate(startTime, enrolledAtDate);
};

type GetCareActivityEnrolledAtDateProps = {
  rpmEnrolledAtDate: MomentInput | undefined;
  enrollmentSummary: AccountMedicalServiceEnrollmentSummary | undefined;
  ccmOnlyEnrollmentEnabled: boolean;
};

// Mirrors the backend's ccm_time_without_rpm?: a CCM-enrolled patient who is not in RPM counts time from the CCM enrollment date.
export const getCareActivityEnrolledAtDate = ({
  rpmEnrolledAtDate,
  enrollmentSummary,
  ccmOnlyEnrollmentEnabled
}: GetCareActivityEnrolledAtDateProps): Moment | undefined => {
  const countsFromCcmEnrollment =
    ccmOnlyEnrollmentEnabled &&
    enrollmentSummary?.rpmStatus !== RpmStatusTypes.ENROLLED &&
    enrollmentSummary?.ccmStatus === CcmStatusTypes.ENROLLED;
  const enrolledAtDate = countsFromCcmEnrollment ? enrollmentSummary?.ccmEnrolledAt : rpmEnrolledAtDate;
  return enrolledAtDate ? moment(enrolledAtDate) : undefined;
};

type IsAwaitingCcmEnrolledAtDateProps = GetCareActivityEnrolledAtDateProps & {
  enrollmentSummaryLoading: boolean;
};

// Without an RPM date, the CCM date the activity counts from only arrives with the enrollment summary.
export const isAwaitingCcmEnrolledAtDate = ({
  rpmEnrolledAtDate,
  enrollmentSummary,
  ccmOnlyEnrollmentEnabled,
  enrollmentSummaryLoading
}: IsAwaitingCcmEnrolledAtDateProps): boolean => {
  const hasRpmEnrolledAtDate = Boolean(rpmEnrolledAtDate) && moment(rpmEnrolledAtDate).isValid();
  return ccmOnlyEnrollmentEnabled && !hasRpmEnrolledAtDate && !enrollmentSummary && enrollmentSummaryLoading;
};

export type AiSummaryAction = 'UNCHANGED' | 'EDITED' | 'DELETED';

export type AiSummaryLaunchMethod = 'BUTTON_CLICK' | 'DEVICE_AI_SUMMARY' | 'ALL_DEVICES_AI_SUMMARY' | 'NO_AI_SUMMARY';

export type AiSummaryFormEntry = {
  original: string;
  final: string;
  action: AiSummaryAction;
  rating?: number | null;
  aiSummaryRequestTimestamp?: string;
  aiSummaryReceivedTimestamp?: string;
  aiSummaryTotalTokens?: number;
};

export type CareActivityFormValues = {
  mainCategory: string;
  type: string;
  category: string;
  subCategory?: string | null;
  careTeamTaskTitle?: string | null;
  careTeamTaskId?: string | null;
  startTime: TruentityDateInput;
  timeMinutes?: number | null;
  contactMode?: string | null;
  shareWithProvider?: boolean | null;
  readingTypes?: RpmDeviceReadingType[] | null;
  aiSummaries?: Partial<Record<RpmDeviceReadingType, AiSummaryFormEntry>>;
  launchMethod?: AiSummaryLaunchMethod;
  note: string;
};

export const READING_TYPE_TO_HEALTH_TYPE: Partial<Record<RpmDeviceReadingType, VitalsHealthTypes>> = {
  [RpmDeviceReadingType.BLOOD_PRESSURE]: VitalsHealthTypes.BloodPressure,
  [RpmDeviceReadingType.BLOOD_GLUCOSE]: VitalsHealthTypes.BloodGlucose,
  [RpmDeviceReadingType.HEART_RATE]: VitalsHealthTypes.HeartRate
};

export const HEALTH_TYPE_TO_READING_TYPE: Partial<Record<VitalsHealthTypes, RpmDeviceReadingType>> = {
  [VitalsHealthTypes.BloodPressure]: RpmDeviceReadingType.BLOOD_PRESSURE,
  [VitalsHealthTypes.BloodGlucose]: RpmDeviceReadingType.BLOOD_GLUCOSE,
  [VitalsHealthTypes.HeartRate]: RpmDeviceReadingType.HEART_RATE
};

export const getDefaultVitalsSummaryDateRange = (): { startDate: Date; endDate: Date } => {
  const endDate = new Date();
  const startDate = moment(endDate).subtract(DEFAULT_VITALS_SUMMARY_RANGE_MONTHS, 'months').toDate();
  return { startDate, endDate };
};

type ValidateCareActivityTitleProps = {
  values: CareActivityFormValues;
  careActivityTitleOptions?: { [key: string]: SystemOptionType };
  formMode: 'ADD' | 'UPDATE';
};

export const validateCareActivityInputs = ({
  values,
  careActivityTitleOptions,
  formMode
}: ValidateCareActivityTitleProps): string | null => {
  if (careActivityTitleOptions) {
    const isTitleValid = (values.category?.length ?? 0) > 0;
    if (!isTitleValid) {
      return 'Failed to save the Activity. Please enter a valid Description.';
    }

    const isSubTitleValid =
      values.category && careActivityTitleOptions
        ? (careActivityTitleOptions[values.category]?.nestedOptions?.length ?? 0) > 0
          ? (values.subCategory?.length ?? 0) > 0
          : true
        : true;
    if (!isSubTitleValid) {
      return 'Failed to save the Activity. Please enter a valid Supporting Description.';
    }

    if ((values.note ?? '').trim().length > 0 && formMode === 'ADD') {
      const isShareWithProviderValid = values.shareWithProvider !== null;
      if (!isShareWithProviderValid) {
        return "Failed to save the Activity. Please choose one from 'Share with Provider' or 'Do Not Share with Provider'";
      }
    }

    const isContactModeValid = (values.contactMode?.length ?? 0) > 0;
    if (!isContactModeValid) {
      return 'Failed to save the Activity. Please choose a Contact Mode.';
    } else {
      return null;
    }
  } else {
    return null;
  }
};

export const ReviewNoteCategoryTypeColor = new Map<string, MuiColor>([
  [ReviewNoteCategoryType.INTRODUCTORY, 'info'],
  [ReviewNoteCategoryType.INTERVENTION, 'secondary'],
  [ReviewNoteCategoryType.ASSESSMENT, 'primary'],
  [ReviewNoteCategoryType.GENERAL, 'warning']
]);

export const parseTemplate = (template: string): string[] => {
  const regex = /<<([^>]+)>>/g;
  let match;
  const placeholders: string[] = [];

  while ((match = regex.exec(template)) !== null) {
    placeholders.push(match[1]);
  }

  return placeholders;
};

export const generateInputsFromTemplate = (placeholders: string[]): TemplateInput[] => {
  return placeholders.map(placeholder => ({
    key: placeholder,
    value: ''
  }));
};

export const replaceTemplatePlaceholders = (template: DynamicTemplateType): string => {
  let updatedText = template.templateText;

  template.inputs.forEach(input => {
    const regex = new RegExp(`<<${input.key}>>`, 'g');
    updatedText = updatedText.replace(regex, input.value);
  });

  return updatedText;
};

export const showFollowupModal = (params: TaskFollowUpEditModalType | GeneralFollowUpEditModalType) => {
  const { category, followUp, accountAssignments, showModal, onComplete } = params;

  if (category === FollowUpCategoryTypes.GENERAL) {
    const { truentityId, rpmStatus, doNotCall, patientPhone } = params;

    const modal = showModal(FollowUpAddEditDialog, {
      title: 'Update Follow Up On',
      hideDialog: () => {
        modal.hide();
      },
      handleRefetchAndClose: () => {
        modal.hide();
        onComplete?.();
      },
      truentityId: truentityId || '',
      rpmStatus: rpmStatus,
      doNotCall: doNotCall || false,
      followupsData: [followUp],
      accountAssignments: accountAssignments,
      isUpdateModal: true,
      isUpdateModalOnly: true,
      patientPhone: patientPhone
    });
  } else {
    const assignees: RelyingPartyAdminData[] = accountAssignments.map(assignment => assignment.relyingPartyAdmin as RelyingPartyAdminData);

    const modal = showModal(FollowUpTaskAddEditDialog, {
      title: 'Update Follow Task',
      hideDialog: () => {
        modal.hide();
      },
      category: category,
      formMode: FollowupFormModeTypes.UPDATE,
      accountAssignees: assignees,
      onUpdated: () => {
        modal.hide();
        onComplete?.();
      },
      followUpTaskData: followUp
    });
  }
};

export const checkIsCriticalAlert = (alertLabel: RpmAlertsLabelTypes) => {
  return [RpmAlertsLabelTypes.CRITICALLY_HIGH, RpmAlertsLabelTypes.CRITICALLY_LOW, RpmAlertsLabelTypes.NO_READINGS].includes(alertLabel);
};

export const splitSearchTerm = (text: string) => {
  const trimmed = text.trim();
  if (!trimmed) return;

  const words = trimmed.split(/\s+/);
  let firstName = '';
  let lastName = '';

  if (words.length === 1) {
    firstName = words[0];
  } else if (words.length > 1) {
    firstName = words[0];
    lastName = words.slice(1).join(' ');
  }
  return { firstName, lastName };
};

export const followupVisitDueStatusColorsMapper = new Map<string, { muiColor: MuiColor; colorCode: string }>([
  [FollowupVisitDueStatusTypes.OVERDUE, { muiColor: 'error', colorCode: color.red900 }],
  [FollowupVisitDueStatusTypes.UPCOMING, { muiColor: 'warning', colorCode: color.orangeDark }],
  [FollowupVisitDueStatusTypes.NONE_DUE, { muiColor: 'default', colorCode: color.grey500 }],
  [FollowupVisitDueStatusTypes.COMPLETED, { muiColor: 'success', colorCode: color.green800 }]
]);

export const formatCamelCase = (input: string, specialCases: Record<string, string>): string => {
  const words = input
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .split(/\s+/);

  const formattedWords = words.map(word => {
    const lowerCaseWord = word.toLowerCase();
    return specialCases[lowerCaseWord] || lowerCaseWord.charAt(0).toUpperCase() + lowerCaseWord.slice(1);
  });

  return formattedWords.join(' ');
};

export const getAvailableTabs = (getAccountVitalsDataAvailability: VitalsDataAvailabilityType) => {
  const isBloodGlucoseDataAvailable = getAccountVitalsDataAvailability?.isBloodGlucoseDataAvailable;
  const isBloodPressureDataAvailable = getAccountVitalsDataAvailability?.isBloodPressureDataAvailable;
  const isHeartRateDataAvailable = getAccountVitalsDataAvailability?.isHeartRateDataAvailable;

  const availableTabs: VitalsHealthTypes[] = [];

  if (isBloodPressureDataAvailable) availableTabs.push(VitalsHealthTypes.BloodPressure);
  if (isBloodGlucoseDataAvailable) availableTabs.push(VitalsHealthTypes.BloodGlucose);
  if (isHeartRateDataAvailable) availableTabs.push(VitalsHealthTypes.HeartRate);

  return availableTabs;
};

export const getVisitStatusColor = (status: RpmStatusTypes | CcmStatusTypes): 'primary' | 'success' | 'error' => {
  const statusColorMap: Record<string, 'primary' | 'success' | 'error'> = {
    [RpmStatusTypes.IS_CANDIDATE]: 'primary',
    [RpmStatusTypes.SCHEDULED_FOR_ENROLLMENT]: 'primary',
    [RpmStatusTypes.ENROLLED]: 'success',
    [RpmStatusTypes.UNENROLLED]: 'error',
    [CcmStatusTypes.ATTRIBUTED_ELSEWHERE]: 'error',
    [CcmStatusTypes.IN_PROGRESS]: 'primary',
    [CcmStatusTypes.ELIGIBLE]: 'primary',
    [CcmStatusTypes.INELIGIBLE]: 'error',
    [CcmStatusTypes.SCHEDULED]: 'primary'
  };

  return statusColorMap[status] || 'primary';
};

export const getCompletedBillingIntervals = (secs: number): number => Math.min(Math.floor((secs ?? 0) / 1200), 3);

export const isCcmRoutable = (summary: AccountMedicalServiceEnrollmentSummary | undefined): boolean =>
  Boolean(summary?.ccmEnrolled) && summary?.ccmBillable !== false;

export const getAutoRoutedMedicalService = (summary: AccountMedicalServiceEnrollmentSummary | undefined): ActivityMainTypeEnum | null => {
  if (!summary) return null;
  const { rpmEnrolled, rpmMonthToDateSecs, ccmMonthToDateSecs } = summary;
  const ccmRoutable = isCcmRoutable(summary);
  if (rpmEnrolled && ccmRoutable) {
    return getCompletedBillingIntervals(ccmMonthToDateSecs) <= getCompletedBillingIntervals(rpmMonthToDateSecs)
      ? ActivityMainTypeEnum.CCM
      : ActivityMainTypeEnum.RPM;
  }
  if (ccmRoutable) return ActivityMainTypeEnum.CCM;
  if (rpmEnrolled) return ActivityMainTypeEnum.RPM;
  return null;
};
